import { createContext } from 'react';
import { useLocation } from 'react-router-dom';
import { z } from 'zod';

import {
  SetSearchParamsOverride,
  useStateFromSearchParams,
} from '@/hooks/useStateFromSearchParams';
import { appGraphIndexPath } from '@/utils/constants';
import { useLocalStorageState } from '@/utils/use-local-storage-state';

export enum StudioMode {
  CHAT = 'chat',
  GRAPH = 'graph',
}

export const STUDIO_MODE_LOCAL_STORAGE_KEY = 'ls:studio:mode';

export const useStudioMode = (setSearchParams?: SetSearchParamsOverride) => {
  const location = useLocation();
  const isGraphPage = location.pathname.includes(appGraphIndexPath);

  const [lastStudioMode, setLastStudioMode] = useLocalStorageState(
    STUDIO_MODE_LOCAL_STORAGE_KEY,
    StudioMode.GRAPH,
    z.enum([StudioMode.CHAT, StudioMode.GRAPH])
  );

  // Setting default as empty string causes the set query params hook to not set any default mode.
  const defaultStudioMode = isGraphPage
    ? ((lastStudioMode ?? StudioMode.GRAPH) as string)
    : '';
  const [studioMode, _setStudioMode] = useStateFromSearchParams(
    'mode',
    defaultStudioMode,
    setSearchParams
  );

  const setStudioMode = (mode: StudioMode) => {
    _setStudioMode(mode);
    setLastStudioMode(mode);
  };

  return {
    lastStudioMode,
    studioMode: studioMode as StudioMode,
    setStudioMode,
  };
};

export const StudioModeContext = createContext<{
  studioMode: StudioMode;
  setStudioMode: (mode: StudioMode) => void;
}>({
  studioMode: StudioMode.GRAPH,
  setStudioMode: () => {},
});
